import { useEffect, useRef } from 'react';
import { useAppDispatch } from '../../store';
import { fetchAnalytics } from '../../store/entities/analytics';
import { DashboardTileDto } from '@api/models/dashboard/DashboardTileDto';
import { DOMAIN_CONFIG_MAP } from './analyticsDomainRegistry';

const tileReduxKey = (tileId: string) => `tile:${tileId}`;

export const useTileFetching = (tiles: DashboardTileDto[], loaded: boolean, loading: boolean) => {
  const dispatch = useAppDispatch();
  const fetchedTileIdsRef = useRef(new Set<string>());

  const fetchTileData = (tile: DashboardTileDto) => {
    const domainConfig = DOMAIN_CONFIG_MAP[tile.domain];
    if (!domainConfig) return;
    const formValues = domainConfig.fromTileFilters(tile.filters ?? {});
    const params = domainConfig.toQueryParams(formValues, tile.groupBy ?? domainConfig.defaultGroupBy);
    dispatch(fetchAnalytics(tileReduxKey(tile.id), () => domainConfig.fetchAnalytics(params)));
  };

  useEffect(() => {
    if (!loaded) return;
    tiles.forEach((tile) => {
      if (fetchedTileIdsRef.current.has(tile.id)) return;
      fetchedTileIdsRef.current.add(tile.id);
      fetchTileData(tile);
    });
    // fetchTileData closes over only stable values (dispatch + module-level imports)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiles, loaded]);

  useEffect(() => {
    if (loading) {
      fetchedTileIdsRef.current.clear();
    }
  }, [loading]);

  const handleRetryTile = (tile: DashboardTileDto) => {
    fetchedTileIdsRef.current.delete(tile.id);
    fetchTileData(tile);
  };

  return { tileReduxKey, handleRetryTile };
};
